import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { FC } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import SvgIcon from '@mui/material/SvgIcon';

import {
  PAYMENT_BUTTON_TEXT,
  CANCEL_BUTTON_TEXT,
  NICKNAME_ON_CARD_CANNOT_INCLUDE_CARD_NUMBER_ERROR,
  PAYMENT_METHOD_SETTINGS_TITLE,
} from '../../pay-and-store/constants';
import PageTitle from '../../../ui/PageTitle/PageTitle';
import BackButton from '../../../ui/BackButton/BackButton';
import FormField from '../../../ui/FormField/FormField';
import noop from '../../../utils/function/noop';
import Button from '../../../ui/Button/Button';
import useFormConfigManager from '../../../hooks/useFormConfigManager/useFormConfigManager';
import {
  TEXT_CONTENT_IDS,
  TITLE_IDS,
} from '../../../utils/element-ids';
import fieldHasCardNumber from '../../../utils/function/fieldHasCardNumber';
import { isInvalidNickname } from '../../../utils/function/nicknameUtils';
import Checkbox from '../../../ui/Checkbox/Checkbox';
import DropdownV2 from '../../../ui/Dropdown/DropdownV2';
import { AppContext } from '../../contextStore/AppContext';
import Disclaimer from '../../../components/Disclaimer/Disclaimer';
import { AmountLine } from '../../components/AmountLine/AmountLine';
import Divider from '../../../ui/Divider/Divider';
import { getCardSvg } from '../../checkout/components/Wallet/utils/getCardSvg';
import IconRenderer from '../../../ui/Icons/IconRenderer';
import { CardCategoriesConfig } from '../../../shared/types/CardCategories/CardCategoriesConfig';
import { MedicationOption } from '../../../shared/types/CardCategories/MedicationOption';
import ManufacturerGuestCardFieldContainer from '../../add-payment-method/components/ManufacturerCard/ManufacturerGuestCardFieldContainer';

type SycurioPhoneEntryBaseProps = {
  nickname?: string;
  cardHolderName: string;
  manufacturerCard: boolean;
  savePaymentMethod: boolean;
  paymentMethodId: string;
  isDefault: boolean;
  cardType: string;
  lastFour: string;
  expYear: string;
  expMonth: string;
  avsPostalCode: string;
  medication: string;
};

export type SycurioSubmitFormData =
  SycurioPhoneEntryBaseProps & {
    token: string;
    hasCapturedSycurioPhoneEntry: boolean;
    country?: string;
  };
type SycurioPaymentMethodFormKeys = keyof SycurioSubmitFormData;
type SycurioPaymentMethodFormValues =
  SycurioSubmitFormData[keyof SycurioSubmitFormData];

type SycurioSubmitFormErrors = Partial<
  Record<keyof SycurioSubmitFormData, string>
>;

type PaymentFormDataProps = Omit<
  SycurioPhoneEntryBaseProps,
  'paymentMethodId'
>;

export type SycurioPhoneEntrySubmitProps = {
  formTitle: string;
  medicationOptions: MedicationOption[];
  cardCategoriesConfigObject: CardCategoriesConfig;
  isGuestUser: boolean;
  showSaveFeatureBlock: boolean;
  paymentMethodId: string;
  amount: number;
  sycurioData: Omit<
    SycurioSubmitFormData,
    | 'token'
    | 'hasCapturedSycurioPhoneEntry'
    | 'manufacturerCard'
    | 'savePaymentMethod'
    | 'isDefault'
    | 'nickname'
    | 'paymentMethodId'
  >;
  onBackClick?: () => void;
  onCancel: () => void;
  onSubmit: (data: SycurioPaymentMethodFormSubmitData) => void;
};
const validate = ({
  nickname,
  manufacturerCard,
  medication,
}: PaymentFormDataProps): SycurioSubmitFormErrors => {
  const formErrors: SycurioSubmitFormErrors = {};

  if (isInvalidNickname(nickname)) {
    formErrors.nickname = 'Please enter a valid card nickname.';
  }
  if (nickname && fieldHasCardNumber(nickname)) {
    formErrors.nickname =
      NICKNAME_ON_CARD_CANNOT_INCLUDE_CARD_NUMBER_ERROR;
  }
  if (manufacturerCard && medication === '') {
    formErrors.medication =
      'A medication selection is required.';
  }

  return formErrors;
};

export type SycurioPaymentMethodFormSubmitData = {
  nameOnCard: string;
  nickname?: string;
  manufacturerCard: boolean;
  savePaymentMethod: boolean;
  isDefault: boolean;
  paymentMethodId: string;
  last4: string;
  medication: string;
};

const SycurioPhoneEntrySubmitView: FC<
  SycurioPhoneEntrySubmitProps
> = ({
  isGuestUser,
  showSaveFeatureBlock,
  paymentMethodId,
  formTitle,
  amount,
  sycurioData,
  onBackClick,
  onCancel = noop,
  onSubmit,
  medicationOptions,
  cardCategoriesConfigObject,
}) => {
  const { setData } = useContext(AppContext);
  const [formErrors, setFormErrors] =
    useState<SycurioSubmitFormErrors>({});

  const safeMedicationOptions = useMemo(
    () =>
      Array.isArray(medicationOptions) ? medicationOptions : [],
    [medicationOptions],
  );

  const [formData, setFormData] = useState<PaymentFormDataProps>(
    {
      cardHolderName: sycurioData.cardHolderName,
      nickname: '',
      manufacturerCard: false,
      savePaymentMethod: false,
      cardType: sycurioData.cardType,
      isDefault: false,
      lastFour: `••••    ••••    ••••    ${sycurioData.lastFour}`,
      expYear: sycurioData.expYear,
      expMonth: sycurioData.expMonth,
      avsPostalCode: sycurioData.avsPostalCode,
      medication: '',
    },
  );

  const paymentMethodIconType = getCardSvg(
    sycurioData?.cardType ?? 'genericcard',
    sycurioData?.cardType,
  );

  const setFormFields = (
    fields: Partial<SycurioSubmitFormData>,
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      ...fields,
    }));
  };

  const handleOnSubmit = useCallback(() => {
    const validations = validate(formData);
    setFormErrors(validations);
    if (Object.keys(validations).length) {
      return;
    }

    onSubmit({
      ...sycurioData,
      nickname: formData.nickname,
      nameOnCard: formData.cardHolderName,
      manufacturerCard: formData.manufacturerCard,
      savePaymentMethod: formData.savePaymentMethod,
      isDefault: formData.isDefault,
      paymentMethodId,
      last4: sycurioData.lastFour,
      medication: formData.medication,
    });
  }, [
    formData.medication,
    formData.nickname,
    formData.cardHolderName,
    formData.manufacturerCard,
    formData.isDefault,
    formData.savePaymentMethod,
    paymentMethodId,
    sycurioData.lastFour,
    sycurioData.cardType,
    sycurioData.country,
    sycurioData.expMonth,
    sycurioData.expYear,
    sycurioData.lastFour,
    sycurioData.avsPostalCode,
  ]);
  const handleOnChange = (
    key: SycurioPaymentMethodFormKeys,
    value: SycurioPaymentMethodFormValues,
  ) => {
    setFormFields({ [key]: value });

    const isUpdatedToManufacturerCard =
      key === 'manufacturerCard' && value;

    if (isUpdatedToManufacturerCard) {
      setFormFields({ isDefault: false });
    }
    const isDefaultKey = key === 'isDefault';
    if (isDefaultKey && value) {
      setFormFields({
        manufacturerCard: false,
        medication: '',
      });
    }
  };
  const handleOnBlur = (
    key: SycurioPaymentMethodFormKeys,
    value: SycurioPaymentMethodFormValues,
  ) => {
    const newFormValues = { ...formData, [key]: value };

    const validationResult = validate(newFormValues);

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [key]: validationResult[key],
    }));

    setFormFields(newFormValues);
  };

  const { showActionsOnForm, title } = useFormConfigManager({
    title: formTitle,
    titleTestId: TITLE_IDS.PAY_AND_STORE_FORM_TITLE,
    backActionConfig: {
      label: 'Back',
      testId: TEXT_CONTENT_IDS.PAY_AND_STORE_CARD_BACK_BUTTON,
      handler: onBackClick,
    },
    primaryActionConfig: {
      label: PAYMENT_BUTTON_TEXT,
      testId: TEXT_CONTENT_IDS.MAKE_PAYMENT_SUBMIT_BUTTON,
      handler: handleOnSubmit,
    },
    secondaryActionConfig: {
      label: CANCEL_BUTTON_TEXT,
      testId: TEXT_CONTENT_IDS.MAKE_PAYMENT_CANCEL_BUTTON,
      handler: useCallback(() => {
        onCancel();
      }, []),
    },
    showBackButton: !!onBackClick,
  });

  useEffect(() => {
    setData({ isLanguageDropdownDisabled: true });

    return () => {
      setData({ isLanguageDropdownDisabled: false });
    };
  }, []);

  return (
    <Box>
      {showActionsOnForm && onBackClick ? (
        <BackButton
          label="Back"
          onClick={onBackClick}
        />
      ) : null}
      <PageTitle
        title={title}
        titleId={TITLE_IDS.PAY_AND_STORE_FORM_TITLE}
      />
      <Box sx={{ my: '24px' }}>
        <Typography
          style={{
            fontSize: '20.25px',
            fontWeight: 700,
            lineHeight: '24px',
          }}
          data-testid={
            TEXT_CONTENT_IDS.PAYMENT_TELEPHONIC_SUBMIT_VIEW_SUB_TITLE
          }
        >
          Card details
        </Typography>
      </Box>
      <Box sx={{ my: '24px' }}>
        <Typography variant="body2">
          All fields are required unless marked optional.
        </Typography>
      </Box>
      <FormField
        id="nameOnCard"
        label="Name on card"
        placeholder=""
        value={formData.cardHolderName}
        errorMessage={formErrors.cardHolderName}
        required
        autocomplete="name"
        onChange={noop}
        disabled
      />
      <FormField
        id="cardNumber"
        label="Card number"
        value={formData.lastFour}
        disabled
        onChange={noop}
        endAdornment={
          <SvgIcon
            data-testid="sycurioPaymentMethodIcon"
            component={paymentMethodIconType}
            width="47px"
            height="26px"
            viewBox="0 0 42 26"
            preserveAspectRatio="xMidYMid meet"
          />
        }
      />
      <Grid
        container
        className="gridCell"
        spacing={2}
      >
        <Grid size={{ xs: 6 }}>
          <FormField
            id="expiration"
            label="Expiration date"
            inputMode="numeric"
            placeholder="MM / YY"
            disabled
            value={`${formData.expMonth} / ${formData.expYear}`}
            required
            onChange={noop}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <FormField
            id="securityCode"
            type="number"
            inputMode="numeric"
            label="Security code"
            placeholder="***"
            disabled
            value="***"
            required
            onChange={noop}
            endAdornment={
              <IconRenderer
                testId="sycurioCvvIcon"
                name="cvvIcon"
              />
            }
          />
        </Grid>
      </Grid>
      {sycurioData.avsPostalCode !== '' && (
        <Grid
          container
          direction="column"
          sx={{
            gap: '12px',
          }}
        >
          <DropdownV2
            value="United States"
            disabled
            id="country"
            label="Country"
          />
          <FormField
            id="zipCode"
            label="ZIP code"
            placeholder="12345"
            value={formData.avsPostalCode}
            required
            disabled
            autocomplete="name"
            onChange={noop}
          />
        </Grid>
      )}

      {!isGuestUser && showSaveFeatureBlock ? (
        <Grid
          container
          direction="column"
          sx={{
            marginTop: '16px',
          }}
        >
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <Typography
                style={{ fontWeight: 700 }}
                data-testid={
                  TEXT_CONTENT_IDS.PAYMENT_METHOD_SETTINGS_LABEL
                }
              >
                {PAYMENT_METHOD_SETTINGS_TITLE}
              </Typography>
            </FormLabel>

            <Checkbox
              id="savePaymentMethod"
              value={formData.savePaymentMethod}
              label="Save for future use"
              testId={
                TEXT_CONTENT_IDS.MAKE_PAYMENT_SAVE_FOR_FUTURE_CHECKBOX_CARD
              }
              onChange={(value) => {
                handleOnChange('savePaymentMethod', value);
              }}
            />
            <ManufacturerGuestCardFieldContainer
              cardCategoriesConfigObject={
                cardCategoriesConfigObject
              }
              manufacturerCardCheckboxProps={{
                id: 'isManufacturerCard',
                value: formData.manufacturerCard,
                onChange: (value: boolean): void => {
                  handleOnChange('manufacturerCard', value);
                },
              }}
              medicationSelectorDropdownProps={{
                id: 'medicationDropdown',
                value: formData.medication,
                options: safeMedicationOptions,
                errorMessage: formErrors.medication,
                onChange: (value: string) => {
                  handleOnChange('medication', value);
                },
                onBlur: (value: string) => {
                  handleOnBlur('medication', value);
                },
              }}
            />
            {formData.savePaymentMethod ? (
              <>
                {!formData.manufacturerCard ? (
                  <Checkbox
                    id="default"
                    value={formData.isDefault}
                    label="Set as default payment method"
                    testId={
                      TEXT_CONTENT_IDS.MAKE_PAYMENT_SET_AS_DEFAULT_CHECKBOX
                    }
                    onChange={(value) => {
                      handleOnChange('isDefault', value);
                    }}
                  />
                ) : null}
                <FormField
                  id="cardNickname"
                  label="Card nickname"
                  hintText="(Optional)"
                  helperText="Card nickname must be 30 characters or less and can contain letters and numbers only."
                  value={formData.nickname || ''}
                  errorMessage={formErrors.nickname}
                  maxLength={30}
                  onChange={(value) => {
                    handleOnChange('nickname', value);
                  }}
                  onBlur={(value) => {
                    handleOnBlur('nickname', value);
                  }}
                />
              </>
            ) : null}
          </FormControl>
        </Grid>
      ) : null}
      <Grid style={{ marginTop: '12px' }}>
        <Divider />
      </Grid>
      <AmountLine
        amount={amount}
        testId={TEXT_CONTENT_IDS.MAKE_PAYMENT_AMOUNTLINE_LABEL}
      />
      <Disclaimer actionType="make_payment" />
      {showActionsOnForm ? (
        <Grid
          container
          direction="column"
          sx={{ gap: '16px', marginTop: '24px' }}
          position="sticky"
          display="absolute"
        >
          <Button
            onClick={handleOnSubmit}
            variant="contained"
            color="primary"
          >
            {PAYMENT_BUTTON_TEXT}
          </Button>
          <Button
            isLoading={false}
            onClick={onCancel}
            variant="contained"
            color="secondary"
          >
            {CANCEL_BUTTON_TEXT}
          </Button>
        </Grid>
      ) : null}
    </Box>
  );
};

export default SycurioPhoneEntrySubmitView;
